import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { ApiFehler, api, fehlertext } from '../api'
import { useAktion, useGemerkt, useLaden } from '../hooks'
import ArtikelAuswahl from '../komponenten/ArtikelAuswahl'
import Meldungen from '../komponenten/Meldungen'
import { tageLesen, wochen, zahlLesen, zahlText } from '../format'
import { artikelPfad } from '../pfade'
import type {
  Artikel,
  Artikelgruppe,
  ArtikelgruppeEingabe,
  Lieferant,
  LieferantArtikel,
  LieferantEingabe,
  Mengenregel,
  MengenregelWerte,
  RegelFeld,
  Staffel,
  WirkQuelle,
  WirksamWert,
} from '../typen'

/**
 * Lieferanten - die Stammdaten, die die Bestellrechnung braucht und die es in keinem
 * SelectLine-Report gibt. Der Forecast des Chefs hat seit dem 04.09.2026 eine eigene Seite.
 *
 * Das Formular steht nur, wenn ein Lieferant gewählt oder „Neuer Lieferant" gedrückt ist; beim
 * Öffnen ist der erste Lieferant vorgewählt, damit die Seite mit Inhalt beginnt.
 *
 * Der bequemere Weg ist der Regel-Agent („DS Smith: mindestens 30 Paletten, Staffeln 50/75/100
 * Tausend, vier Wochen Puffer"). Diese Seite ist für den Blick darauf und für gezielte Korrekturen.
 *
 * Zahlenfelder halten Text, bis gespeichert wird - so lässt sich „231,50" tippen, ohne dass das
 * Komma beim Rendern verschwindet. Gelesen wird erst beim Speichern, mit einer Meldung je Fehler.
 *
 * Artikelgruppen (02.11.2026, Wunsch Frau Barth): ein Teil des Sortiments mit eigenen Werten - Mindestabruf,
 * Vorlauf, Puffer, Produktionszeit, Abruffrist. Ebenen: Standard → Lieferant → Gruppe → Artikel, leer = geerbt.
 * Die Artikel stehen als Tabelle mit Bezeichnung, Blatt, Gruppe (direkt in der Zeile wählbar) und den Werten,
 * die für sie wirklich gelten, samt Ebene. Ein älteres Backend ohne Gruppen zeigt die Tabelle aus den
 * Stammdaten mit den Werten des Lieferanten; der Gruppenabschnitt sagt dann, dass es sie noch nicht gibt.
 */

type ZahlFeld = RegelFeld

/** Die Mengenregel im Formular: Regel leer = geerbt, Paletten als Text. */
type MengenFormular = {
  abruf_mengenregel: '' | Mengenregel
  abruf_paletten: string
  nachauflage_mengenregel: '' | Mengenregel
  nachauflage_paletten: string
}

const MENGEN_LEER: MengenFormular = { abruf_mengenregel: '', abruf_paletten: '', nachauflage_mengenregel: '', nachauflage_paletten: '' }

/** Die beiden Arten mit ihren Feldern - Abruf aus dem Herstellerbestand, Nachauflage als neue Produktion. */
const MENGEN_ARTEN = [
  { art: 'abruf', titel: 'Abruf', regel: 'abruf_mengenregel', paletten: 'abruf_paletten' },
  { art: 'nachauflage', titel: 'Nachauflage', regel: 'nachauflage_mengenregel', paletten: 'nachauflage_paletten' },
] as const

type MengenArt = (typeof MENGEN_ARTEN)[number]['art']

/** Die Wahl im Formular; den fertigen Satz („genau 33 Paletten“) rechnet das Backend (`*_mengenregel_text`). */
const MENGENREGEL_TITEL: Record<Mengenregel, string> = { mindestens: 'mindestens', genau: 'genau', vielfache: 'ein Vielfaches von' }

function mengenFormularAus(w: MengenregelWerte): MengenFormular {
  return {
    abruf_mengenregel: w.abruf_mengenregel ?? '',
    abruf_paletten: zahlText(w.abruf_paletten ?? null),
    nachauflage_mengenregel: w.nachauflage_mengenregel ?? '',
    nachauflage_paletten: zahlText(w.nachauflage_paletten ?? null),
  }
}

/** Die Mengenregel lesen; leer wird null (geerbt). Paletten ohne Regel wären eine Zahl ohne Bedeutung. */
function mengenAus(form: MengenFormular): MengenregelWerte | string {
  const werte: MengenregelWerte = {}
  for (const art of MENGEN_ARTEN) {
    const text = form[art.paletten].trim()
    const paletten = text === '' ? null : zahlLesen(text)
    if (text !== '' && (paletten === null || paletten <= 0 || !Number.isInteger(paletten)))
      return `${art.titel}: Paletten als ganze Zahl über 0 angeben.`
    if (paletten !== null && !form[art.regel])
      return `${art.titel}: zu ${paletten} Paletten auch die Regel wählen (mindestens, genau, ein Vielfaches).`
    werte[art.regel] = form[art.regel] || null
    werte[art.paletten] = paletten
  }
  return werte
}

/** Das Formular: Zahlen als Text, alles andere wie in der Eingabe. */
type Formular = Omit<LieferantEingabe, ZahlFeld | 'id' | keyof MengenregelWerte> & Record<ZahlFeld, string> & MengenFormular

// Zeiten in Tagen gespeichert, aber Frau Barth denkt in Wochen: „13 Wo" wird angenommen, daneben steht die Umrechnung.
const ZAHLFELDER: {
  name: ZahlFeld
  titel: string
  /** Spaltenkopf und Tooltip */
  kurz: string
  pflicht: boolean
  wochen?: boolean
}[] = [
  { name: 'mindestabruf_paletten', titel: 'Mindestabruf (Pal.)', kurz: 'Mindestabruf', pflicht: false },
  { name: 'abruf_vorlauf_tage', titel: 'Vorlauf Abruf (Tage)', kurz: 'Vorlauf Abruf', pflicht: true, wochen: true },
  { name: 'abruf_puffer_tage', titel: 'Puffer Abruf (Tage)', kurz: 'Puffer Abruf', pflicht: true, wochen: true },
  { name: 'puffer_tage', titel: 'Puffer Nachauflage (Tage)', kurz: 'Puffer Nachauflage', pflicht: true, wochen: true },
  { name: 'produktionszeit_tage', titel: 'Produktionszeit (Tage)', kurz: 'Produktionszeit', pflicht: false, wochen: true },
  { name: 'abruffrist_monate', titel: 'Abruffrist (Monate)', kurz: 'Abruffrist', pflicht: false },
]

function feldVon(name: ZahlFeld) {
  return ZAHLFELDER.find((f) => f.name === name) ?? ZAHLFELDER[0]
}

/** Ein Regelwert zum Lesen: Zeiten in Wochen, dazu Paletten und Monate; leer bleibt leer. */
function wertText(feld: ZahlFeld, wert: number | string | null | undefined): string {
  if (wert === null || wert === undefined || wert === '') return ''
  if (typeof wert === 'string') return wert
  if (feldVon(feld).wochen) return wochen(wert)
  if (feld === 'mindestabruf_paletten') return `${wert} Pal.`
  if (feld === 'abruffrist_monate') return `${wert} Mon.`
  return String(wert)
}

const PREIS_GILT = { bestellung: 'Bestellung', lieferung: 'Lieferung' } as const

/** Die Zahlenfelder lesen; mit `mitPflicht` müssen die Pflichtfelder (des Lieferanten) gefüllt sein. */
function zahlenAus(werte: Record<ZahlFeld, string>, mitPflicht: boolean): Record<ZahlFeld, number | null> | string {
  const zahlen = {} as Record<ZahlFeld, number | null>
  for (const feld of ZAHLFELDER) {
    const text = werte[feld.name]
    const wert = feld.wochen ? tageLesen(text) : zahlLesen(text)
    if (text.trim() !== '' && wert === null) return `${feld.titel}: „${text}“ ist keine Zahl.`
    if (mitPflicht && feld.pflicht && wert === null) return `${feld.titel} angeben.`
    if (wert !== null && (wert < 0 || !Number.isInteger(wert))) return `${feld.titel}: ganze Zahl ab 0.`
    zahlen[feld.name] = wert
  }
  return zahlen
}

const LEER: Formular = {
  name: '',
  kurzname: null,
  mindestabruf_paletten: '',
  abruf_vorlauf_tage: '7',
  abruf_puffer_tage: '14',
  puffer_tage: '28',
  produktionszeit_tage: '',
  abruffrist_monate: '',
  preis_gilt: 'bestellung',
  bemerkung: null,
  aktiv: true,
  ...MENGEN_LEER,
}

function formularAus(l: Lieferant): Formular {
  return {
    name: l.name,
    kurzname: l.kurzname,
    mindestabruf_paletten: zahlText(l.mindestabruf_paletten),
    abruf_vorlauf_tage: zahlText(l.abruf_vorlauf_tage),
    abruf_puffer_tage: zahlText(l.abruf_puffer_tage),
    puffer_tage: zahlText(l.puffer_tage),
    produktionszeit_tage: zahlText(l.produktionszeit_tage),
    abruffrist_monate: zahlText(l.abruffrist_monate),
    preis_gilt: l.preis_gilt,
    bemerkung: l.bemerkung,
    aktiv: l.aktiv,
    ...mengenFormularAus(l),
  }
}

/** Formular lesen; bei einem Fehler die Meldung statt der Eingabe. */
function eingabeAus(form: Formular, id: number | null): LieferantEingabe | string {
  if (!form.name.trim()) return 'Name angeben.'
  const zahlen = zahlenAus(form, true)
  if (typeof zahlen === 'string') return zahlen
  const mengen = mengenAus(form)
  if (typeof mengen === 'string') return mengen
  return {
    ...mengen,
    id,
    name: form.name.trim(),
    kurzname: form.kurzname?.trim() || null,
    preis_gilt: form.preis_gilt,
    bemerkung: form.bemerkung,
    aktiv: form.aktiv,
    mindestabruf_paletten: zahlen.mindestabruf_paletten,
    abruf_vorlauf_tage: zahlen.abruf_vorlauf_tage as number,
    abruf_puffer_tage: zahlen.abruf_puffer_tage as number,
    puffer_tage: zahlen.puffer_tage as number,
    produktionszeit_tage: zahlen.produktionszeit_tage,
    abruffrist_monate: zahlen.abruffrist_monate,
  }
}

/**
 * Eine Staffelzeile in Bearbeitung - Text, bis gespeichert wird. Die Schwelle zählt in Stück oder (seit
 * 02.11.2026) in Paletten, genau eins: „x Paletten der Gruppe Wickler kosten …“ (Göran).
 */
interface StaffelZeile {
  schwelle: string
  einheit: 'stueck' | 'paletten'
  preis_je_1000: string
  artikelgruppe: string
}

function staffelZeilen(staffeln: Staffel[]): StaffelZeile[] {
  return staffeln.map((s) => {
    const paletten = s.ab_paletten !== null && s.ab_paletten !== undefined
    return {
      schwelle: zahlText(paletten ? s.ab_paletten : s.ab_stueck),
      einheit: paletten ? 'paletten' : 'stueck',
      preis_je_1000: zahlText(s.preis_je_1000),
      artikelgruppe: s.artikelgruppe ?? '',
    }
  })
}

/** Staffelzeilen lesen; jede Zeile trägt ihre Artikelgruppe, „ab 0 Stück“ ist die Basisstaffel. */
function staffelnAus(zeilen: StaffelZeile[]): Staffel[] | string {
  const ergebnis: Staffel[] = []
  for (const [i, z] of zeilen.entries()) {
    const ab = zahlLesen(z.schwelle)
    const preis = zahlLesen(z.preis_je_1000)
    if (ab === null || ab < 0) return `Stufe ${i + 1}: Schwelle angeben - ab wie viel Stück oder Paletten (0 für den Grundpreis).`
    if (preis === null || preis <= 0) return `Stufe ${i + 1}: Preis je 1.000 angeben.`
    ergebnis.push({
      ab_stueck: z.einheit === 'stueck' ? ab : null,
      ab_paletten: z.einheit === 'paletten' ? ab : null,
      preis_je_1000: preis,
      artikelgruppe: z.artikelgruppe.trim() || null,
      bemerkung: null,
    })
  }
  return ergebnis
}

// --- Artikelgruppen --------------------------------------------------------------------------------

/** Das Formular einer Gruppe: Zahlen als Text, leer = wie der Lieferant. */
type GruppenFormular = {
  id: number | null
  name: string
  bemerkung: string
  preis_gilt: '' | 'bestellung' | 'lieferung'
} & Record<ZahlFeld, string> &
  MengenFormular

const GRUPPE_LEER: GruppenFormular = {
  id: null,
  name: '',
  bemerkung: '',
  preis_gilt: '',
  mindestabruf_paletten: '',
  abruf_vorlauf_tage: '',
  abruf_puffer_tage: '',
  puffer_tage: '',
  produktionszeit_tage: '',
  abruffrist_monate: '',
  ...MENGEN_LEER,
}

function gruppenFormularAus(g: Artikelgruppe): GruppenFormular {
  const form: GruppenFormular = {
    ...GRUPPE_LEER,
    ...mengenFormularAus(g),
    id: g.id,
    name: g.name,
    bemerkung: g.bemerkung ?? '',
    preis_gilt: g.preis_gilt ?? '',
  }
  for (const feld of ZAHLFELDER) form[feld.name] = zahlText(g[feld.name])
  return form
}

/** Das ganze Formular, leere Felder als null - das Backend setzt sie damit auf „geerbt“ zurück. */
function gruppenEingabeAus(form: GruppenFormular, vorhanden: Artikelgruppe[]): ArtikelgruppeEingabe | string {
  const name = form.name.trim()
  if (!name) return 'Name der Gruppe angeben.'
  // Ohne Id ändert das Backend eine Gruppe gleichen Namens - das wäre hier ein stilles Überschreiben.
  const doppelt = vorhanden.find((g) => g.name.trim().toLowerCase() === name.toLowerCase() && g.id !== form.id)
  if (doppelt) return `Eine Gruppe „${doppelt.name}“ gibt es bei diesem Lieferanten schon.`
  const zahlen = zahlenAus(form, false)
  if (typeof zahlen === 'string') return zahlen
  const mengen = mengenAus(form)
  if (typeof mengen === 'string') return mengen
  return { id: form.id, name, bemerkung: form.bemerkung.trim() || null, preis_gilt: form.preis_gilt || null, ...zahlen, ...mengen }
}

/** Die vier Zeiten, die je Artikel gelten - die Spalten der Artikeltabelle. */
const WIRK_SPALTEN: ZahlFeld[] = ['produktionszeit_tage', 'puffer_tage', 'abruf_vorlauf_tage', 'abruf_puffer_tage']

const QUELLE: Record<WirkQuelle, { marke: string | null; satz: string }> = {
  artikel: { marke: 'Artikel', satz: 'am Artikel eingetragen (Stammdaten)' },
  gruppe: { marke: 'Gruppe', satz: 'von der Artikelgruppe' },
  lieferant: { marke: null, satz: 'wie der Lieferant' },
  standard: { marke: 'Std.', satz: 'Vorgabe aus den Einstellungen' },
}

/**
 * Die Artikel des Lieferanten für die Tabelle. Ein älteres Backend liefert nur Nummern - dann kommen
 * Bezeichnung und Blatt aus den Stammdaten und die Werte vom Lieferanten (vom Artikel, wo er eigene hat).
 */
function artikelZeilen(l: Lieferant, stamm: Artikel[]): LieferantArtikel[] {
  if (l.artikel_details) return l.artikel_details
  const nachNummer = new Map(stamm.map((a) => [a.artikelnummer, a]))
  const vom = (wert: number | null | undefined, artikelwert: number | null | undefined): WirksamWert =>
    artikelwert !== null && artikelwert !== undefined ? { wert: artikelwert, quelle: 'artikel' } : { wert: wert ?? null, quelle: 'lieferant' }
  return l.artikel.map((nummer) => {
    const a = nachNummer.get(nummer)
    const gruppe = (l.gruppen ?? []).find((g) => g.artikel.includes(nummer))
    return {
      artikelnummer: nummer,
      bezeichnung: a?.bezeichnung ?? '',
      kurzname: a?.kurzname ?? null,
      art: a?.art ?? 'material',
      gebindegruppe: a?.gebindegruppe ?? null,
      aktiv: a?.aktiv ?? true,
      artikelgruppe: gruppe?.name ?? null,
      artikelgruppe_id: gruppe?.id ?? null,
      wirksam: {
        produktionszeit_tage: vom(l.produktionszeit_tage, a?.lieferzeit_tage),
        puffer_tage: vom(l.puffer_tage, a?.puffer_tage),
        abruf_vorlauf_tage: vom(l.abruf_vorlauf_tage, null),
        abruf_puffer_tage: vom(l.abruf_puffer_tage, null),
      },
    }
  })
}

/** Nach Gruppe (ohne Gruppe zuletzt), dann Blatt, dann Nummer. */
function sortiert(zeilen: LieferantArtikel[]): LieferantArtikel[] {
  const text = (a: string | null, b: string | null) => (a === b ? 0 : a === null ? 1 : b === null ? -1 : a.localeCompare(b, 'de'))
  return [...zeilen].sort(
    (a, b) =>
      text(a.artikelgruppe, b.artikelgruppe) ||
      text(a.gebindegruppe, b.gebindegruppe) ||
      a.artikelnummer.localeCompare(b.artikelnummer, 'de', { numeric: true }),
  )
}

export default function LieferantenSeite() {
  // Von der Disposition („als Lieferant anlegen"): Name und Materialien des Herstellers kommen mit,
  // damit aus dem Hersteller mit einem Klick ein Lieferant wird.
  const [suchparameter, setSuchparameter] = useSearchParams()
  const [vorbelegt, setVorbelegt] = useState<{
    name: string
    materialien: string[]
  } | null>(() => {
    const name = suchparameter.get('name')
    if (!name) return null
    const materialien = (suchparameter.get('materialien') ?? '').split(',').filter(Boolean)
    return { name, materialien }
  })
  const [lieferanten, setLieferanten] = useState<Lieferant[]>([])
  const [form, setForm] = useState<Formular>(LEER)
  const [gewaehlt, setGewaehlt] = useState<Lieferant | null>(null)
  // „Neuer Lieferant" gedrückt: das leere Formular steht, ohne dass ein Lieferant gewählt ist.
  const [neu, setNeu] = useState(false)
  // Der erste Lieferant wird genau einmal vorgewählt - nach dem Löschen bleibt die Seite leer, bis man wählt.
  const vorgewaehlt = useRef(false)
  // Welcher Lieferant zuletzt offen war - zurück auf diesem Reiter steht man wieder bei ihm statt beim ersten
  // der Liste (Frau Barth, Rückmeldung 20.10.2026: die Ansicht soll den Reiterwechsel überstehen).
  const [gemerkteId, setGemerkteId] = useGemerkt<number | null>('lieferanten.gewaehlt', null)
  const [staffeln, setStaffeln] = useState<StaffelZeile[]>([])
  const [zuordnen, setZuordnen] = useState('')
  const aktion = useAktion()
  const { fehler, erfolg, arbeitet, setFehler, setErfolg, ausfuehren } = aktion
  const { daten: artikelDaten } = useLaden<Artikel[]>(() => api.artikel(), [])
  const artikel = artikelDaten ?? []

  const laden = useCallback(() => {
    api
      .lieferanten()
      .then((werte) => {
        setLieferanten(werte)
        setGewaehlt((alt) => (alt ? (werte.find((l) => l.id === alt.id) ?? null) : null))
      })
      .catch((f) => setFehler(fehlertext(f)))
  }, [setFehler])

  useEffect(laden, [laden])

  useEffect(() => {
    if (!vorbelegt) return
    setSuchparameter({}, { replace: true })
    const vorhanden = lieferanten.find((l) => l.name.trim().toLowerCase() === vorbelegt.name.trim().toLowerCase())
    if (vorhanden) {
      auswaehlen(vorhanden)
      setVorbelegt(null)
      return
    }
    setGewaehlt(null)
    setNeu(true)
    setForm({ ...LEER, name: vorbelegt.name })
    setStaffeln([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vorbelegt, lieferanten])

  // Ohne Vorgabe aus der Adresse: den ersten Lieferanten zeigen, statt mit einem leeren Formular zu beginnen.
  useEffect(() => {
    if (vorgewaehlt.current || vorbelegt || gewaehlt || neu || lieferanten.length === 0) return
    vorgewaehlt.current = true
    auswaehlen(lieferanten.find((l) => l.id === gemerkteId) ?? lieferanten[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lieferanten, vorbelegt, gewaehlt, neu])

  function auswaehlen(l: Lieferant) {
    setGewaehlt(l)
    setGemerkteId(l.id)
    setNeu(false)
    setForm(formularAus(l))
    setStaffeln(staffelZeilen(l.staffeln))
    setErfolg(null)
    setFehler(null)
  }

  async function loeschen(id: number) {
    // Der Server lehnt das Löschen ab, solange Materialien zugeordnet sind, und sagt welche: mit dem
    // Lieferanten fallen Puffer, Vorlauf und Mindestabruf weg, und ihre Termine springen. Erst nach
    // dieser Rückfrage wird es eine Entscheidung (Befund 19.09.2026).
    let mitArtikeln = false
    try {
      await api.lieferantLoeschen(id)
    } catch (f) {
      if (!(f instanceof ApiFehler && f.status === 409 && f.message.includes('liefert noch'))) {
        setFehler(fehlertext(f))
        return
      }
      if (!window.confirm(`${f.message}\n\nTrotzdem löschen?`)) return
      mitArtikeln = true
    }
    await ausfuehren(async () => {
      if (mitArtikeln) await api.lieferantLoeschen(id, true)
      setGewaehlt(null)
      setNeu(false)
      setForm(LEER)
      setStaffeln([])
      laden()
    }, 'Lieferant gelöscht. Die Artikel bleiben, nur die Bestellregeln entfallen.')
  }

  async function speichern() {
    const eingabe = eingabeAus(form, gewaehlt?.id ?? null)
    if (typeof eingabe === 'string') {
      setFehler(eingabe)
      return
    }
    await ausfuehren(async () => {
      let gespeichert = await api.lieferantSpeichern(eingabe)
      let meldung = `${gespeichert.name} gespeichert.`
      if (vorbelegt && vorbelegt.materialien.length > 0) {
        const fehlend = vorbelegt.materialien.filter((nummer) => !gespeichert.artikel.includes(nummer))
        if (fehlend.length > 0) {
          gespeichert = await api.lieferantArtikel(gespeichert.id, fehlend)
          meldung += ` ${fehlend.length} Material(ien) zugeordnet: ${fehlend.join(', ')}.`
        }
        setVorbelegt(null)
      }
      setErfolg(meldung)
      setGewaehlt(gespeichert)
      setNeu(false)
      setForm(formularAus(gespeichert))
      laden()
    })
  }

  async function staffelnSpeichern() {
    if (!gewaehlt) return
    const liste = staffelnAus(staffeln)
    if (typeof liste === 'string') {
      setFehler(liste)
      return
    }
    await ausfuehren(async () => {
      const neu = await api.staffelnSetzen(gewaehlt.id, liste)
      setGewaehlt(neu)
      setStaffeln(staffelZeilen(neu.staffeln))
      laden()
    }, 'Preisstaffeln gespeichert.')
  }

  async function artikelZuordnen() {
    if (!gewaehlt || !zuordnen) return
    await ausfuehren(async () => {
      const neu = await api.lieferantArtikel(gewaehlt.id, [zuordnen])
      setGewaehlt(neu)
      setZuordnen('')
      laden()
    })
  }

  /**
   * Einen Artikel in eine andere Gruppe stellen: `PUT …/gruppen/{id}/artikel` ersetzt die Mitglieder, also die
   * neue Liste der Zielgruppe schicken. Hält die alte Gruppe den Artikel danach noch (oder geht er auf „ohne
   * Gruppe“), wird er dort ausgetragen - ein Artikel ist in höchstens einer Gruppe.
   */
  async function gruppeWechseln(z: LieferantArtikel, neuId: number | null) {
    if (!gewaehlt) return
    const lieferant = gewaehlt
    const gruppen = lieferant.gruppen ?? []
    const alt = gruppen.find((g) => g.id === z.artikelgruppe_id) ?? gruppen.find((g) => g.artikel.includes(z.artikelnummer))
    const ziel = gruppen.find((g) => g.id === neuId)
    if ((alt?.id ?? null) === (ziel?.id ?? null)) return
    await ausfuehren(
      async () => {
        let stand = lieferant
        if (ziel) {
          const mitglieder = [...new Set([...ziel.artikel, z.artikelnummer])]
          stand = await api.gruppeArtikelSetzen(lieferant.id, ziel.id, mitglieder)
        }
        const altNachher = alt && (stand.gruppen ?? []).find((g) => g.id === alt.id)
        if (alt && altNachher?.artikel.includes(z.artikelnummer)) {
          stand = await api.gruppeArtikelSetzen(
            lieferant.id,
            alt.id,
            altNachher.artikel.filter((n) => n !== z.artikelnummer),
          )
        }
        setGewaehlt(stand)
        laden()
      },
      ziel ? `${z.artikelnummer} gehört jetzt zur Gruppe ${ziel.name}.` : `${z.artikelnummer} ist jetzt ohne Gruppe.`,
    )
  }

  function staffelAendern(i: number, teil: Partial<StaffelZeile>) {
    setStaffeln((alt) => alt.map((x, j) => (j === i ? { ...x, ...teil } : x)))
  }

  const textfeld = (name: 'name' | 'kurzname', titel: string) => (
    <label className="feld">
      <span className="hinweis">{titel}</span>
      <input
        type="text"
        style={{ width: 180 }}
        value={form[name] ?? ''}
        onChange={(e) => setForm((alt) => ({ ...alt, [name]: e.target.value }))}
      />
    </label>
  )

  const gruppen = gewaehlt?.gruppen
  const zeilen = gewaehlt ? sortiert(artikelZeilen(gewaehlt, artikel)) : []

  return (
    <>
      <h2>Lieferanten</h2>
      <p className="hinweis" style={{ maxWidth: '80ch' }}>
        Ein Lieferant ist die Bestelleinheit: Mindestabruf über sein Sortiment (oder je Artikelgruppe), Vorlauf und
        Puffer, Produktionszeit, Preisstaffeln. Schneller geht es auf der Seite Regeln in einem Satz - hier steht, was
        daraus geworden ist. Der Forecast des Chefs und Plan-Ist stehen auf der Seite <Link to="/forecast">Forecast</Link>.
      </p>

      <Meldungen fehler={fehler} erfolg={erfolg} />

      <div className="zwei-spalten">
        <div className="karte">
          <h3>Lieferanten</h3>
          <table>
            <tbody>
              {lieferanten.map((l) => (
                <tr key={l.id} className={gewaehlt?.id === l.id ? 'gewaehlt' : ''}>
                  <td>
                    <button onClick={() => auswaehlen(l)}>{l.name}</button>
                  </td>
                  <td className="hinweis">
                    {l.artikel.length} Artikel
                    {l.gruppen && l.gruppen.length > 0
                      ? ` · ${l.gruppen.length} ${l.gruppen.length === 1 ? 'Gruppe' : 'Gruppen'}`
                      : ''}
                    {l.abruf_mengenregel_text ? ` · Abruf ${l.abruf_mengenregel_text}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            style={{ marginTop: 8 }}
            onClick={() => {
              setGewaehlt(null)
              setNeu(true)
              setForm(LEER)
              setStaffeln([])
              setErfolg(null)
              setFehler(null)
            }}
          >
            Neuer Lieferant
          </button>
        </div>

        {!gewaehlt && !neu && (
          <div className="karte">
            <p className="leer">
              {lieferanten.length === 0
                ? 'Noch kein Lieferant angelegt. „Neuer Lieferant" legt den ersten an.'
                : 'Links einen Lieferanten wählen oder „Neuer Lieferant" drücken.'}
            </p>
          </div>
        )}

        {(gewaehlt || neu) && (
          <div className="karte">
            <h3>{gewaehlt ? gewaehlt.name : 'Neuer Lieferant'}</h3>
            {!gewaehlt && vorbelegt && vorbelegt.materialien.length > 0 && (
              <p className="hinweis">
                Aus der Disposition: beim Speichern werden {vorbelegt.materialien.join(', ')} diesem Lieferanten
                zugeordnet. Vorlauf, Puffer und Produktionszeit bitte prüfen.
              </p>
            )}
            <div className="leiste">
              {textfeld('name', 'Name')}
              {textfeld('kurzname', 'Kurzname')}
              <ZahlFelder werte={form} onChange={(name, text) => setForm((alt) => ({ ...alt, [name]: text }))} />
              <label className="feld">
                <span className="hinweis">Preis gilt bei</span>
                <select
                  value={form.preis_gilt}
                  onChange={(e) =>
                    setForm((alt) => ({
                      ...alt,
                      preis_gilt: e.target.value as 'bestellung' | 'lieferung',
                    }))
                  }
                >
                  <option value="bestellung">Bestellung</option>
                  <option value="lieferung">Lieferung</option>
                </select>
              </label>
            </div>
            <div className="leiste">
              <MengenFelder
                werte={form}
                leer={() => 'Mindestabruf als Untergrenze'}
                platzhalter={form.mindestabruf_paletten.trim() || undefined}
                onChange={(teil) => setForm((alt) => ({ ...alt, ...teil }))}
              />
            </div>
            <div className="leiste">
              <button className="haupt" onClick={() => void speichern()}>
                Speichern
              </button>
              {gewaehlt && <span className="hinweis">Ein geänderter Name benennt diesen Lieferanten um.</span>}
              {gewaehlt && (
                <button
                  style={{ marginLeft: 'auto' }}
                  onClick={() => {
                    if (
                      !window.confirm(`${gewaehlt.name} löschen? Die Artikel bleiben, nur die Bestellregeln entfallen.`)
                    )
                      return
                    void loeschen(gewaehlt.id)
                  }}
                >
                  Löschen
                </button>
              )}
            </div>

            {gewaehlt && (
              <>
                <ArtikelgruppenAbschnitt
                  key={gewaehlt.id}
                  lieferant={gewaehlt}
                  aktion={aktion}
                  onGeaendert={(neu) => {
                    if (neu) setGewaehlt(neu)
                    laden()
                  }}
                />

                <h4>Artikel dieses Lieferanten</h4>
                {zeilen.length === 0 ? (
                  <p className="hinweis">Noch keiner zugeordnet.</p>
                ) : (
                  <ArtikelTabelle
                    lieferant={gewaehlt}
                    zeilen={zeilen}
                    gesperrt={arbeitet}
                    onGruppe={(z, id) => void gruppeWechseln(z, id)}
                  />
                )}
                <div className="leiste">
                  <ArtikelAuswahl
                    artikel={artikel.filter((a) => a.art === 'material' && !gewaehlt.artikel.includes(a.artikelnummer))}
                    wert={zuordnen}
                    onChange={setZuordnen}
                    leer="Artikel zuordnen …"
                  />
                  <button disabled={!zuordnen} onClick={() => void artikelZuordnen()}>
                    Zuordnen
                  </button>
                </div>

                <h4>Preisstaffeln (je 1.000 Stück)</h4>
                <p className="hinweis">
                  „ab 0 Stück" ist der Grundpreis. Eine Stufe zählt ab Stück oder ab Paletten - über alle Artikel ihrer
                  Artikelgruppe zusammen; ohne Artikelgruppe gilt sie für das ganze Sortiment.
                </p>
                {gruppen && gruppen.length > 0 && (
                  <datalist id="staffel-artikelgruppen">
                    {gruppen.map((g) => (
                      <option key={g.id} value={g.name} />
                    ))}
                  </datalist>
                )}
                <table>
                  <thead>
                    <tr>
                      <th className="zahl">ab</th>
                      <th></th>
                      <th className="zahl">€ je 1.000</th>
                      <th>Artikelgruppe</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffeln.map((s, i) => {
                      const gruppeText = s.artikelgruppe.trim()
                      const unbekannt =
                        gruppen !== undefined &&
                        gruppeText !== '' &&
                        !gruppen.some((g) => g.name.trim().toLowerCase() === gruppeText.toLowerCase())
                      return (
                        <tr key={i}>
                          <td>
                            <input
                              type="text"
                              inputMode="decimal"
                              style={{ width: 90, textAlign: 'right' }}
                              value={s.schwelle}
                              onChange={(e) => staffelAendern(i, { schwelle: e.target.value })}
                            />
                          </td>
                          <td>
                            <select
                              value={s.einheit}
                              onChange={(e) => staffelAendern(i, { einheit: e.target.value as StaffelZeile['einheit'] })}
                            >
                              <option value="stueck">Stück</option>
                              <option value="paletten">Paletten</option>
                            </select>
                          </td>
                          <td>
                            <input
                              type="text"
                              inputMode="decimal"
                              style={{ width: 80, textAlign: 'right' }}
                              value={s.preis_je_1000}
                              onChange={(e) =>
                                staffelAendern(i, {
                                  preis_je_1000: e.target.value,
                                })
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              style={{ width: 160 }}
                              value={s.artikelgruppe}
                              placeholder="alle"
                              list={gruppen && gruppen.length > 0 ? 'staffel-artikelgruppen' : undefined}
                              title="Eine Gruppe dieses Lieferanten wählen oder einen Namen eintippen; leer = ganzes Sortiment"
                              onChange={(e) =>
                                staffelAendern(i, {
                                  artikelgruppe: e.target.value,
                                })
                              }
                            />
                            {unbekannt && (
                              <div className="hinweis">keine Gruppe dieses Namens - die Stufe greift erst, wenn es sie gibt</div>
                            )}
                          </td>
                          <td>
                            <button onClick={() => setStaffeln((alt) => alt.filter((_, j) => j !== i))}>Entfernen</button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                <div className="leiste">
                  <button
                    onClick={() =>
                      setStaffeln((alt) => [...alt, { schwelle: '', einheit: 'stueck', preis_je_1000: '', artikelgruppe: '' }])
                    }
                  >
                    Stufe hinzufügen
                  </button>
                  <button className="haupt" onClick={() => void staffelnSpeichern()}>
                    Staffeln speichern
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  )
}

/** Die Zahlenfelder eines Lieferanten oder einer Gruppe; Zeiten nehmen „13 Wo“ an und zeigen die Umrechnung. */
function ZahlFelder({
  werte,
  onChange,
  platzhalter,
}: {
  werte: Record<ZahlFeld, string>
  onChange: (name: ZahlFeld, text: string) => void
  /** bei einer Gruppe: der Wert des Lieferanten, der bei leerem Feld gilt */
  platzhalter?: Partial<Record<ZahlFeld, string>>
}) {
  return (
    <>
      {ZAHLFELDER.map((feld) => {
        const text = werte[feld.name]
        const tage = feld.wochen && text.trim() !== '' ? tageLesen(text) : null
        const vorgabe = platzhalter?.[feld.name]
        const tipp = [
          feld.wochen && 'Tage oder Wochen, zum Beispiel „13 Wo“',
          platzhalter && `leer = wie der Lieferant (${vorgabe || 'keiner'})`,
        ]
          .filter(Boolean)
          .join(' · ')
        return (
          <label key={feld.name} className="feld" title={tipp || undefined}>
            <span className="hinweis">
              {feld.titel}
              {tage !== null && <> = {wochen(tage)}</>}
            </span>
            <input
              type="text"
              inputMode="numeric"
              style={{ width: 80, textAlign: 'right' }}
              value={text}
              placeholder={vorgabe}
              onChange={(e) => onChange(feld.name, e.target.value)}
            />
          </label>
        )
      })}
    </>
  )
}

/**
 * Die Mengenregel je Art: Regel und Paletten. Gezählt wird über alle Artikel des Lieferanten (bzw. der Gruppe)
 * zusammen - so rechnet der Bestellvorschlag Berry mit genau 33 Paletten je Abruf und 33 oder 66 je Nachauflage.
 */
function MengenFelder({
  werte,
  leer,
  platzhalter,
  onChange,
}: {
  werte: MengenFormular
  /** Beschriftung der leeren Wahl je Art: beim Lieferanten der Mindestabruf, bei einer Gruppe der Wert des Lieferanten */
  leer: (art: MengenArt) => string
  /** Paletten bei leerem Feld: der Mindestabruf */
  platzhalter?: string
  onChange: (teil: Partial<MengenFormular>) => void
}) {
  return (
    <>
      {MENGEN_ARTEN.map((art) => (
        <label
          key={art.regel}
          className="feld"
          title="Über alle Artikel zusammen. „Ein Vielfaches von 33“ heißt 33, 66 … Paletten; leere Paletten = Mindestabruf."
        >
          <span className="hinweis">Menge je {art.titel}</span>
          <span className="leiste" style={{ gap: 6, margin: 0, flexWrap: 'nowrap' }}>
            <select
              value={werte[art.regel]}
              onChange={(e) => onChange({ [art.regel]: e.target.value as '' | Mengenregel })}
            >
              <option value="">
                {leer(art.art)}
              </option>
              {(Object.keys(MENGENREGEL_TITEL) as Mengenregel[]).map((regel) => (
                <option key={regel} value={regel}>
                  {MENGENREGEL_TITEL[regel]}
                </option>
              ))}
            </select>
            <input
              type="text"
              inputMode="numeric"
              aria-label={`Paletten je ${art.titel}`}
              style={{ width: 70, textAlign: 'right' }}
              value={werte[art.paletten]}
              placeholder={werte[art.regel] ? platzhalter : undefined}
              disabled={!werte[art.regel] && werte[art.paletten] === ''}
              onChange={(e) => onChange({ [art.paletten]: e.target.value })}
            />
            <span className="hinweis">Pal.</span>
          </span>
        </label>
      ))}
    </>
  )
}

/** Ein wirksamer Wert mit seiner Ebene: Marke „Artikel“/„Gruppe“/„Std.“, vom Lieferanten grau und kursiv. */
function WirksamZelle({
  feld,
  w,
  gruppe,
  lieferant,
}: {
  feld: ZahlFeld
  w: WirksamWert | undefined
  gruppe: string | null
  lieferant: string
}) {
  const titel = feldVon(feld).kurz
  const text = wertText(feld, w?.wert)
  if (!w || text === '') {
    return (
      <td className="zahl leise" title={`${titel}: nicht hinterlegt`}>
        –
      </td>
    )
  }
  const quelle = QUELLE[w.quelle] ?? QUELLE.lieferant
  const woher =
    w.quelle === 'gruppe' && gruppe
      ? `von der Artikelgruppe ${gruppe}`
      : w.quelle === 'lieferant'
        ? `wie der Lieferant ${lieferant}`
        : quelle.satz
  const tage = typeof w.wert === 'number' && feldVon(feld).wochen ? ` = ${w.wert} Tage` : ''
  const geerbt = w.quelle === 'lieferant' || w.quelle === 'standard'
  return (
    <td className={`zahl${geerbt ? ' geerbt' : ''}`} title={`${titel}: ${text}${tage} - ${woher}`}>
      {text}
      {quelle.marke && <span className="marke quelle">{quelle.marke}</span>}
    </td>
  )
}

/** Die Artikel des Lieferanten: was sie sind, auf welchem Blatt, in welcher Gruppe und welche Zeiten für sie gelten. */
function ArtikelTabelle({
  lieferant,
  zeilen,
  gesperrt,
  onGruppe,
}: {
  lieferant: Lieferant
  zeilen: LieferantArtikel[]
  gesperrt: boolean
  onGruppe: (zeile: LieferantArtikel, gruppeId: number | null) => void
}) {
  const gruppen = lieferant.gruppen ?? []
  return (
    <>
      <div className="tabellenrahmen">
        <table>
          <thead>
            <tr>
              <th>Nummer</th>
              <th>Bezeichnung</th>
              <th>Blatt</th>
              <th>Artikelgruppe</th>
              {WIRK_SPALTEN.map((feld) => (
                <th key={feld} className="zahl">
                  {feldVon(feld).kurz}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <tr key={z.artikelnummer} className={z.aktiv ? '' : 'inaktiv'}>
                <td>
                  <Link to={artikelPfad(z.artikelnummer)} title="In den Stammdaten öffnen">
                    {z.artikelnummer}
                  </Link>
                </td>
                <td>
                  {z.bezeichnung || <span className="hinweis">nicht in den Stammdaten</span>}
                  {!z.aktiv && <span className="marke quelle">inaktiv</span>}
                  {z.kurzname && z.kurzname !== z.bezeichnung && <span className="artikel-kurzname">{z.kurzname}</span>}
                </td>
                <td>{z.gebindegruppe ?? <span className="hinweis">ohne Blatt</span>}</td>
                <td>
                  {gruppen.length > 0 ? (
                    <select
                      value={z.artikelgruppe_id ?? ''}
                      disabled={gesperrt}
                      title="Artikelgruppe dieses Artikels - wird sofort gespeichert"
                      onChange={(e) => onGruppe(z, e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">ohne Gruppe</option>
                      {gruppen.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="hinweis">{z.artikelgruppe ?? 'ohne Gruppe'}</span>
                  )}
                </td>
                {WIRK_SPALTEN.map((feld) => (
                  <WirksamZelle
                    key={feld}
                    feld={feld}
                    w={z.wirksam?.[feld]}
                    gruppe={z.artikelgruppe}
                    lieferant={lieferant.name}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="hinweis">
        Zeiten in Wochen, Tage im Tooltip. Marke „Artikel“: am Artikel eingetragen (Stammdaten) · „Gruppe“: von der
        Artikelgruppe · „Std.“: Vorgabe der Einstellungen · ohne Marke, grau: wie der Lieferant.
      </p>
    </>
  )
}

/** Die Artikelgruppen eines Lieferanten: Liste mit ihren Werten, anlegen, ändern, löschen. */
function ArtikelgruppenAbschnitt({
  lieferant: l,
  aktion,
  onGeaendert,
}: {
  lieferant: Lieferant
  aktion: ReturnType<typeof useAktion>
  /** mit dem Lieferanten aus der Antwort, falls es einen gab */
  onGeaendert: (neu?: Lieferant) => void
}) {
  const [formular, setFormular] = useState<GruppenFormular | null>(null)
  const { arbeitet, ausfuehren, setFehler, setErfolg } = aktion
  const gruppen = l.gruppen

  const vomLieferanten = (feld: ZahlFeld) => wertText(feld, l[feld])
  const platzhalter: Partial<Record<ZahlFeld, string>> = {}
  for (const feld of ZAHLFELDER) {
    const wert = l[feld.name]
    if (wert !== null && wert !== undefined) platzhalter[feld.name] = feld.wochen ? wochen(wert) : zahlText(wert)
  }

  async function speichern() {
    if (!formular) return
    const eingabe = gruppenEingabeAus(formular, gruppen ?? [])
    if (typeof eingabe === 'string') {
      setFehler(eingabe)
      return
    }
    await ausfuehren(async () => {
      const neu = await api.gruppeSpeichern(l.id, eingabe)
      setFormular(null)
      onGeaendert(neu)
    }, `Gruppe ${eingabe.name} gespeichert.`)
  }

  async function loeschen(g: Artikelgruppe) {
    const n = g.artikel.length
    const frage = [
      `Gruppe ${g.name} löschen?`,
      n > 0 ? `Ihre ${n} Artikel bleiben bei ${l.name}, ohne Gruppe, und bekommen wieder seine Werte.` : '',
      l.staffeln.some((s) => s.artikelgruppe === g.name) ? `Preisstaffeln für ${g.name} bleiben stehen.` : '',
    ]
    if (!window.confirm(frage.filter(Boolean).join(' '))) return
    await ausfuehren(async () => {
      const neu = await api.gruppeLoeschen(l.id, g.id)
      if (formular?.id === g.id) setFormular(null)
      setErfolg(`Gruppe ${g.name} gelöscht.${neu?.hinweis ? ` ${neu.hinweis}` : ''}`)
      onGeaendert(neu ?? undefined)
    })
  }

  return (
    <>
      <h4>Artikelgruppen</h4>
      <p className="hinweis" style={{ maxWidth: '80ch' }}>
        Werte einer Gruppe gelten für ihre Artikel und übersteuern den Lieferanten; leer = wie der Lieferant. Ein Wert
        am Artikel geht beiden vor.
      </p>
      {gruppen === undefined ? (
        <p className="hinweis">Artikelgruppen liefert das Backend noch nicht.</p>
      ) : (
        <>
          {gruppen.length > 0 && (
            <div className="tabellenrahmen">
              <table>
                <thead>
                  <tr>
                    <th>Gruppe</th>
                    <th></th>
                    <th className="zahl">Artikel</th>
                    {ZAHLFELDER.map((f) => (
                      <th key={f.name} className="zahl">
                        {f.kurz}
                      </th>
                    ))}
                    <th>Preis gilt bei</th>
                  </tr>
                </thead>
                <tbody>
                  {gruppen.map((g) => (
                    <tr key={g.id} className={formular?.id === g.id ? 'gewaehlt' : ''}>
                      <td>
                        <b>{g.name}</b>
                        {g.bemerkung && <span className="artikel-kurzname">{g.bemerkung}</span>}
                      </td>
                      {/* Knöpfe vorn: die Wertespalten sind breit, hinten lägen sie außer Sicht. */}
                      <td>
                        <button disabled={arbeitet} onClick={() => setFormular(gruppenFormularAus(g))}>
                          Bearbeiten
                        </button>{' '}
                        <button className="leise" disabled={arbeitet} onClick={() => void loeschen(g)}>
                          Löschen
                        </button>
                      </td>
                      <td className="zahl" title={g.artikel.join(', ') || 'noch kein Artikel'}>
                        {g.artikel.length}
                      </td>
                      {ZAHLFELDER.map((f) => {
                        const eigen = g[f.name] ?? null
                        return eigen === null ? (
                          <td key={f.name} className="zahl leise" title={`${f.kurz}: nicht eigens gesetzt`}>
                            wie Lieferant
                            <br />({vomLieferanten(f.name) || 'keiner'})
                          </td>
                        ) : (
                          <td key={f.name} className="zahl" title={`${f.kurz}: eigener Wert der Gruppe`}>
                            {wertText(f.name, eigen)}
                          </td>
                        )
                      })}
                      <td className={g.preis_gilt ? '' : 'leise'}>
                        {g.preis_gilt ? (
                          PREIS_GILT[g.preis_gilt]
                        ) : (
                          <>
                            wie Lieferant
                            <br />({PREIS_GILT[l.preis_gilt]})
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {gruppen.length === 0 && !formular && (
            <p className="hinweis">
              Noch keine Gruppe. Eine Gruppe lohnt sich, wenn ein Teil des Sortiments eigene Zeiten, einen eigenen
              Mindestabruf oder eigene Staffeln hat. Artikel kommen danach über die Spalte „Artikelgruppe“ unten hinein.
            </p>
          )}

          {formular ? (
            <div className="pflege-formular">
              <p className="hinweis">
                {formular.id !== null ? 'Gruppe ändern - ein neuer Name benennt sie um. ' : 'Neue Gruppe. '}
                Leere Felder gelten wie beim Lieferanten (grau vorgeschrieben). Mindestabruf und Abruffrist zählen über
                alle Artikel der Gruppe zusammen.
              </p>
              <div className="leiste felder">
                <label className="feld">
                  <span className="hinweis">Name</span>
                  <input
                    type="text"
                    style={{ width: 180 }}
                    value={formular.name}
                    placeholder="z.B. Wickler"
                    onChange={(e) => setFormular((alt) => alt && { ...alt, name: e.target.value })}
                  />
                </label>
                <ZahlFelder
                  werte={formular}
                  platzhalter={platzhalter}
                  onChange={(name, text) => setFormular((alt) => alt && { ...alt, [name]: text })}
                />
                <label className="feld">
                  <span className="hinweis">Preis gilt bei</span>
                  <select
                    value={formular.preis_gilt}
                    onChange={(e) =>
                      setFormular((alt) => alt && { ...alt, preis_gilt: e.target.value as GruppenFormular['preis_gilt'] })
                    }
                  >
                    <option value="">wie Lieferant ({PREIS_GILT[l.preis_gilt]})</option>
                    <option value="bestellung">Bestellung</option>
                    <option value="lieferung">Lieferung</option>
                  </select>
                </label>
                <MengenFelder
                  werte={formular}
                  leer={(art) => `wie Lieferant (${l[`${art}_mengenregel_text`] ?? 'keine Grenze'})`}
                  platzhalter={formular.mindestabruf_paletten.trim() || platzhalter.mindestabruf_paletten}
                  onChange={(teil) => setFormular((alt) => alt && { ...alt, ...teil })}
                />
                <label className="feld" style={{ flex: '1 1 260px' }}>
                  <span className="hinweis">Bemerkung</span>
                  <input
                    type="text"
                    value={formular.bemerkung}
                    onChange={(e) => setFormular((alt) => alt && { ...alt, bemerkung: e.target.value })}
                  />
                </label>
              </div>
              <div className="leiste">
                <button className="haupt" disabled={arbeitet} onClick={() => void speichern()}>
                  Gruppe speichern
                </button>
                <button onClick={() => setFormular(null)}>Abbrechen</button>
              </div>
            </div>
          ) : (
            <div className="leiste">
              <button disabled={arbeitet} onClick={() => setFormular({ ...GRUPPE_LEER })}>
                Neue Gruppe
              </button>
            </div>
          )}
        </>
      )}
    </>
  )
}
